import { Badge, Card, Input, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { Eye, EyeOff, Lock, UsersRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { listGroups, type Group, type GroupVisibility } from '../../lib/teams-groups-api'

const visibilityMeta: Record<
  GroupVisibility,
  { label: string; variant: 'success' | 'gold' | 'neutral'; icon: typeof Eye }
> = {
  public: { label: 'Public', variant: 'success', icon: Eye },
  unlisted: { label: 'Unlisted', variant: 'gold', icon: EyeOff },
  private: { label: 'Private', variant: 'neutral', icon: Lock },
}

const enrollmentLabel: Record<string, string> = {
  open: 'Open',
  request: 'Request to join',
  closed: 'Closed',
}

export function GroupsList() {
  const [search, setSearch] = useState('')
  const [visibility, setVisibility] = useState<GroupVisibility | ''>('')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['groups', { visibility }],
    queryFn: () => listGroups(visibility ? { visibility } : undefined),
  })

  const filtered = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.teamName ?? '').toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
    )
  }, [data, search])

  return (
    <div className="space-y-6">
      <PageHeader
        title="Groups"
        description="Public-facing small groups and gatherings across your ministries."
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Input
          placeholder="Search groups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          aria-label="Search groups"
        />
        <label className="flex items-center gap-2 text-sm text-muted">
          <span>Visibility</span>
          <select
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as GroupVisibility | '')}
            className="h-9 rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            aria-label="Filter by visibility"
          >
            <option value="">All</option>
            <option value="public">Public</option>
            <option value="unlisted">Unlisted</option>
            <option value="private">Private</option>
          </select>
        </label>
      </div>

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading groups…
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load groups.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="py-20 text-center text-sm text-muted">
            {search || visibility
              ? 'No groups match your filters.'
              : 'No groups yet. Create your first group to get started.'}
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <GroupCard key={g.id} group={g} />
          ))}
        </div>
      )}
    </div>
  )
}

function GroupCard({ group }: { group: Group }) {
  const meta = visibilityMeta[group.visibility]
  const VisIcon = meta.icon
  return (
    <Link
      to={`/app/groups/${group.id}`}
      className="group block rounded-xl border border-border bg-card p-5 transition-colors hover:bg-card-hover focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="grid size-10 place-items-center rounded-lg bg-surface-2 text-secondary">
          <UsersRound className="size-5" />
        </div>
        <Badge variant={meta.variant} className="gap-1">
          <VisIcon className="size-3" />
          {meta.label}
        </Badge>
      </div>
      <h3 className="mt-3 font-display text-base font-semibold tracking-tight text-fg">
        {group.name}
      </h3>
      {group.teamName && <p className="text-xs text-dim">{group.teamName}</p>}
      {group.description && (
        <p className="mt-2 line-clamp-2 text-sm text-muted">{group.description}</p>
      )}
      <div className="mt-4 flex items-center justify-between text-xs text-dim">
        <span>
          {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
        </span>
        <span>{enrollmentLabel[group.enrollment] ?? group.enrollment}</span>
      </div>
    </Link>
  )
}
